import * as i0 from '@angular/core';
import { Directive, Input, HostListener, NgModule } from '@angular/core';
import * as i2 from '@angular/material/tooltip';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { Subject, of } from 'rxjs';
import { throttleTime, tap, delay } from 'rxjs/operators';
import * as i3 from '@angular/cdk/overlay';
import '@ff/material-controls/core/interfaces';
import * as i1 from '@ff/material-controls/core/services';
import * as i4 from '@ff/localization';
import { CommonModule } from '@angular/common';

class FFRegexFilterDirective {
    constructor(_configService, _matTooltip, _element, _overlayPositionBuilder, _overlay, _localizationQ) {
        this._configService = _configService;
        this._matTooltip = _matTooltip;
        this._element = _element;
        this._overlayPositionBuilder = _overlayPositionBuilder;
        this._overlay = _overlay;
        this._localizationQ = _localizationQ;
        this._tooltip$ = new Subject();
        this._tooltipThrottleTime = this._configService.getTooltipThrottleTime();
        this._tooltipDuration = this._configService.getTooltipDuration();
        this.filterRegex = null;
        this._tooltip = this._matTooltip;
    }
    ngAfterViewInit() {
        this._tooltipSubscription = this._tooltip$.pipe(throttleTime(this._tooltipThrottleTime), tap(() => {
            var _a;
            if (this._tooltip != null && ((_a = this._tooltip) === null || _a === void 0 ? void 0 : _a.message) != null) {
                if (this._element.nativeElement.tagName.toLowerCase() === 'ngx-mat-select-search') {
                    const childNodes = this._element.nativeElement.children;
                    const elementToConnect = Array.from(childNodes).find(node => node.className.includes('mat-select-search-inner'));
                    if (elementToConnect != null && this._tooltip._overlayRef != null) {
                        const positionStrategy = this._overlayPositionBuilder
                            .flexibleConnectedTo(elementToConnect)
                            .withPositions([{
                                originX: 'center',
                                originY: 'bottom',
                                overlayX: 'center',
                                overlayY: 'center',
                            }]);
                        // Connect position strategy
                        this._tooltip._overlayRef = this._overlay.create({ positionStrategy });
                    }
                }
                this._tooltip.message = this.filterRegex != null ? this._localizationQ.transform(this.filterRegex.title) : '';
                this._tooltip.show();
            }
            return of(void 0);
        }), delay(this._tooltipDuration), tap(_ => {
            var _a;
            (_a = this._tooltip) === null || _a === void 0 ? void 0 : _a.hide();
        })).subscribe();
    }
    ngOnDestroy() {
        var _a;
        (_a = this._tooltipSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
    }
    onInputChange(event) {
        var _a, _b;
        const key = event.data;
        if (key != null && ((_a = this.filterRegex) === null || _a === void 0 ? void 0 : _a.value) != null) {
            const value = key.replace(new RegExp((_b = this.filterRegex) === null || _b === void 0 ? void 0 : _b.value, 'g'), '');
            if (key !== value) {
                if (this._tooltip._isTooltipVisible() === false) {
                    this._tooltip$.next();
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                }
            }
            else {
                this._tooltip.hide();
            }
        }
    }
    onKeyPress(event) {
        var _a;
        const key = event.key;
        if (((_a = this.filterRegex) === null || _a === void 0 ? void 0 : _a.value) == null) {
            return;
        }
        const value = key.replace(new RegExp(this.filterRegex.value, 'g'), '');
        if (key !== value) {
            if (this._tooltip._isTooltipVisible() === false) {
                this._tooltip$.next();
            }
            event.preventDefault();
        }
        else {
            this._tooltip.hide();
        }
    }
    onPaste(event) {
        var _a, _b;
        const clipboardData = event.clipboardData;
        const pastedText = (_a = clipboardData === null || clipboardData === void 0 ? void 0 : clipboardData.getData('text')) === null || _a === void 0 ? void 0 : _a.trim();
        if (pastedText != null && ((_b = this.filterRegex) === null || _b === void 0 ? void 0 : _b.value) != null) {
            // @ts-ignore
            for (const letter of pastedText) {
                const value = letter.replace(new RegExp(this.filterRegex.value, 'g'), '');
                if (letter !== value) {
                    if (this._tooltip._isTooltipVisible() === false) {
                        this._tooltip$.next();
                    }
                    event.preventDefault();
                }
                else {
                    this._tooltip.hide();
                }
            }
        }
    }
    onBlur() {
        this._tooltip.hide();
    }
    onFocusOut() {
        this._tooltip.hide();
    }
}
FFRegexFilterDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFRegexFilterDirective, deps: [{ token: i1.FFMaterialControlsConfigService }, { token: i2.MatTooltip }, { token: i0.ElementRef }, { token: i3.OverlayPositionBuilder }, { token: i3.Overlay }, { token: i4.LocalizationQuery }], target: i0.ɵɵFactoryTarget.Directive });
FFRegexFilterDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FFRegexFilterDirective, selector: "input[ffRegexFilter], textarea[ffRegexFilter], ngx-mat-select-search[ffRegexFilter]", inputs: { filterRegex: "filterRegex" }, host: { listeners: { "input": "onInputChange($event)", "keypress": "onKeyPress($event)", "paste": "onPaste($event)", "blur": "onBlur($event)", "focusout": "onFocusOut($event)" } }, providers: [MatTooltip], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFRegexFilterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[ffRegexFilter], textarea[ffRegexFilter], ngx-mat-select-search[ffRegexFilter]',
                    providers: [MatTooltip],
                }]
        }], ctorParameters: function () { return [{ type: i1.FFMaterialControlsConfigService }, { type: i2.MatTooltip }, { type: i0.ElementRef }, { type: i3.OverlayPositionBuilder }, { type: i3.Overlay }, { type: i4.LocalizationQuery }]; }, propDecorators: { filterRegex: [{
                type: Input
            }], onInputChange: [{
                type: HostListener,
                args: ['input', ['$event']]
            }], onKeyPress: [{
                type: HostListener,
                args: ['keypress', ['$event']]
            }], onPaste: [{
                type: HostListener,
                args: ['paste', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }] } });

class FFRegexFilterModule {
}
FFRegexFilterModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFRegexFilterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FFRegexFilterModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFRegexFilterModule, declarations: [FFRegexFilterDirective], imports: [CommonModule,
        MatTooltipModule], exports: [FFRegexFilterDirective] });
FFRegexFilterModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFRegexFilterModule, providers: [], imports: [[
            CommonModule,
            MatTooltipModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFRegexFilterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatTooltipModule
                    ],
                    exports: [FFRegexFilterDirective],
                    declarations: [FFRegexFilterDirective],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FFRegexFilterDirective, FFRegexFilterModule };
